import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { AfterViewInit, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormControl } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { phoneNumberValidator } from 'src/app/common-components/validators/phone-number.validator';
import { ExtensionOption } from 'src/app/pages/extension-register/models/extension-option.interface';
import { LcsSingleSelectSelectedEvent } from '../../single-select/single-select-base';
import { LcsSingleSelectDirective } from '../../single-select/single-select.directive';
import { ExtensionSelectComponent } from '../component/extension-select.component';
import { ExtensionChipInputOption, ExtensionChipInputType } from './extension-phone-number.model';

@Component({
    providers: [{ multi: true, provide: NG_VALUE_ACCESSOR, useExisting: ExtensionChipInputComponent }],
    selector: 'ndt-extension-chip-input',
    styleUrls: ['./extension-chip-input.component.scss'],
    templateUrl: './extension-chip-input.component.html',
    standalone: false
})
export class ExtensionChipInputComponent implements ControlValueAccessor, AfterViewInit {
   @Input() excludedIds: number[] = [];

   @Input() isRequired: boolean = false;

   @Input() accountId: number;

   @Input() chipLabel: string = 'Phone Number/Extension to Dial';

   @Input() maxChipCount: number | null;

   @Input() set touched(touched: boolean) {
      if (touched === true && !this.extensionChipsControl.touched) {
         this.extensionChipsControl.markAsTouched();
      }
   }

   @Input() extensionType: number = 1;

   @ViewChild('extensionInput') extensionInput: ElementRef<HTMLInputElement>;

   @ViewChild('extensionSelect') autoComplete: ExtensionSelectComponent;

   @ViewChild(LcsSingleSelectDirective)
   singleSelectDirective: LcsSingleSelectDirective<ExtensionOption>;

   extensionChipsControl = new UntypedFormControl();

   extensionInputControl = new UntypedFormControl();

   values: ExtensionChipInputOption[] = [];

   displayExtensions: ExtensionOption[] = [];

   get displayValues(): ExtensionChipInputOption[] {
      return this.values.filter((value) => !value.isDeleted);
   }

   separatorKeysCodes: number[] = [ENTER, COMMA];

   onChange: (values: ExtensionChipInputOption[]) => void;

   onTouched: () => void;

   writeValue(values: ExtensionChipInputOption[]): void {
      if (values) {
         this.values = values;
      } else {
         this.values = [];
      }
      this.extensionChipsControl.setValue(values);
      this.removeErrors();
      this.updateDisplayExtensions();
   }

   registerOnChange(onChange: (values: ExtensionChipInputOption[]) => void): void {
      this.onChange = onChange;
   }

   registerOnTouched(onTouched: () => void): void {
      this.onTouched = onTouched;
   }

   ngAfterViewInit(): void {
      this.updateExtensionInputControlDisabledState();
   }

   removeValue(value: ExtensionChipInputOption): void {
      const values: ExtensionChipInputOption[] = [...this.values];
      const indexToRemove = values.findIndex((v) => {
         if (value.type === ExtensionChipInputType.Extension) {
            return (
               (v.value as ExtensionOption).number === (value.value as ExtensionOption).number &&
               v.stepId === value.stepId
            );
         } else {
            return v.value === value.value && v.stepId === value.stepId;
         }
      });
      if (values[indexToRemove].stepId === 0) {
         values.splice(indexToRemove, 1);
      } else {
         values[indexToRemove].isDeleted = true;
      }
      this.values = values;
      this.updateValueAndControls(this.values);
      this;
   }

   add(event: MatChipInputEvent): void {
      //Don't add if there are more active chips than allowed
      if (this.maxChipCount && this.displayValues.length >= this.maxChipCount) {
         return;
      }

      // Add number only when autocomplete is not open
      if (this.autoComplete.isOpen) {
         const value = event.value;
         const trimmedValue = value.trim();
         if (trimmedValue) {
            const extensionOptionComponent = this.autoComplete.options.find((o) => {
               return (
                  (o.value && o.value.number && o.value.number.toString() === trimmedValue) ||
                  (o.value && o.value.name && o.value.name.toLowerCase() === trimmedValue.toLowerCase()) ||
                  false
               );
            });
            if (extensionOptionComponent && extensionOptionComponent.value) {
               extensionOptionComponent.selectViaInteraction();
            } else {
               const validationResult = phoneNumberValidator()({ value } as AbstractControl);
               if (!validationResult) {
                  const existingPhoneNumbers = this.values
                     .filter((value) => value.type === ExtensionChipInputType.PhoneNumber)
                     .map((v) => v.value as string);
                  if (existingPhoneNumbers.findIndex((v) => v === value) >= 0) {
                     this.showDuplicatePhoneNumberError();
                  } else {
                     const newValue: ExtensionChipInputOption = {
                        stepId: 0,
                        type: ExtensionChipInputType.PhoneNumber,
                        value,
                     };
                     this.values = [...this.values, newValue];
                     this.updateValueAndControls(this.values);
                  }
               } else {
                  this.showInvalidPhoneNumberError();
               }
            }
         }
      }
   }

   selected(event: LcsSingleSelectSelectedEvent<ExtensionOption>): void {
      //Don't add if there are more active chips than allowed
      if (this.maxChipCount && this.displayValues.length >= this.maxChipCount) {
         return;
      }
      if (event.option.value) {
         const newValue: ExtensionChipInputOption = {
            stepId: 0,
            type: ExtensionChipInputType.Extension,
            value: event.option.value,
         };
         this.values = [...this.values, newValue];
         this.updateValueAndControls(this.values);
      }
   }

   removeErrors(): void {
      if (!this.extensionChipsControl.errors?.required) {
         this.extensionChipsControl.setErrors(null);
      }
   }

   showInvalidPhoneNumberError(): void {
      this.extensionChipsControl.setErrors({ invalidPhoneNumber: true });
      this.extensionChipsControl.markAsTouched();
   }

   showDuplicatePhoneNumberError(): void {
      this.extensionChipsControl.setErrors({ duplicatePhoneNumber: true });
      this.extensionChipsControl.markAsTouched();
   }

   updateDisplayExtensions(): void {
      this.displayExtensions = this.displayValues
         .filter((value) => value.type === ExtensionChipInputType.Extension)
         .map((ext) => {
            return ext.value as ExtensionOption;
         });
   }

   updateValueAndControls(values: ExtensionChipInputOption[]): void {
      if (this.autoComplete.panel) {
         this.autoComplete.panel.visible = false;
         this.singleSelectDirective.close();
      }
      this.extensionInput.nativeElement.value = '';
      this.extensionInputControl.setValue(null);
      this.extensionChipsControl.setValue(this.displayValues);
      this.onChange(this.values);
      this.removeErrors();
      this.updateDisplayExtensions();
      this.updateExtensionInputControlDisabledState();
   }

   private updateExtensionInputControlDisabledState() {
      if (this.maxChipCount !== null && this.displayValues.length >= this.maxChipCount) {
         this.extensionInputControl.disable();
      } else {
         this.extensionInputControl.enable();
      }
   }
}
